import { useCallback } from 'react';
import type { ReactElement } from 'react';

import BpkButton, { BUTTON_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-button';
import { withButtonAlignment } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import BpkMobileIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/mobile';

import {
  COMPONENT_NAME,
  ELEMENT_NAME,
  EVENT_TYPE_EVENT_FIELD,
  LOGGER_EVENT_NAME,
  OS_NAMES,
} from '../constants';
import useOnScreen from '../hooks/useOnScreen';
import { getEvent } from '../userTrackingEvents';

import { AppleIcon, AndroidIcon } from './OSIcon';
import { getAppDownloadLink } from './utils';

import type { AppDownLoadLinks, CultureData } from '../Footer';
import type { ProductVertical } from '../constants';
import type { Logger } from '@web-platform/global-components-types/utils';
import type { TrackingEventFn } from '@web-platform/global-components-utils/tracking';

import STYLES from './MobileAppButton.module.scss';

type Props = {
  osName: string;
  pageType: string;
  vertical: ProductVertical;
  cultureData: CultureData;
  appDownLoadLinks: AppDownLoadLinks;
  strings: { getMobileApp: string };
  logger: Logger;
  trackEventFn: TrackingEventFn;
};

const AlignedMobileIcon = withButtonAlignment(BpkMobileIcon);
const AlignedAppleIcon = withButtonAlignment(AppleIcon);
const AlignedAndroidIcon = withButtonAlignment(AndroidIcon);

const MobileAppButton = (props: Props): ReactElement => {
  const {
    appDownLoadLinks,
    cultureData,
    logger,
    osName,
    pageType,
    strings,
    trackEventFn,
    vertical,
  } = props;

  const { getMobileApp } = strings;

  let ButtonIcon;
  switch (osName) {
    case OS_NAMES.ANDROID:
      ButtonIcon = AlignedAndroidIcon;
      break;
    case OS_NAMES.IOS:
      ButtonIcon = AlignedAppleIcon;
      break;
    default:
      ButtonIcon = AlignedMobileIcon;
  }

  const { logOperationalEvent } = logger;
  const onVisible = useCallback(() => {
    logOperationalEvent({
      eventName: LOGGER_EVENT_NAME.APP_CTA_IMPRESSION,
      vertical,
      component: COMPONENT_NAME,
    });

    trackEventFn([
      getEvent({
        eventType: EVENT_TYPE_EVENT_FIELD.EVENT_TYPE_VISIBLE,
        elementName: ELEMENT_NAME.APP_CTA,
        country: cultureData.country,
        locale: cultureData.languageShort,
        currency: cultureData.currencyData?.currency || '',
        osName,
        pageType,
        vertical,
      }),
    ]);
  }, [cultureData, logOperationalEvent, osName, pageType, trackEventFn, vertical]);

  const [ref] = useOnScreen(onVisible);

  const appDownLoadLink = getAppDownloadLink(appDownLoadLinks, osName);
  return (
    <div ref={ref} className={STYLES['mobile-app-button']}>
      <BpkButton
        className={STYLES['mobile-app-button__download']}
        type={BUTTON_TYPES.secondaryOnDark}
        href={appDownLoadLink}
        fullWidth
      >
        <ButtonIcon className={STYLES['mobile-app-button__icon']} />
        {getMobileApp}
      </BpkButton>
    </div>
  );
};

export default MobileAppButton;
